import Add from '@mui/icons-material/Add';
import PasteFromClipboard from '@mui/icons-material/Assignment';
import Clear from '@mui/icons-material/Clear';
import Delete from '@mui/icons-material/Delete';
import Properties from '@mui/icons-material/Description';
import Edit from '@mui/icons-material/Edit';
import FolderOpen from '@mui/icons-material/Folder';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PauseLogo from '@mui/icons-material/Pause';
import ResumeLogo from '@mui/icons-material/PlayArrow';
import Transactions from '@mui/icons-material/Receipt';
import DxDataGrid from '@virtus/components/DxDataGrid';
import { IExtraColumnProps, SchemaType } from '@virtus/components/DxDataGrid/DxDataGrid';
import { DataSourceType } from '@virtus/components/DxDataGrid/utils/mapSchema';
import 'components/GridControl.css';
import { IDataObject, saveData } from 'data/DataConnector';
import {
  Column,
  Editing,
  Export,
  Grouping,
  GroupItem,
  GroupPanel,
  Pager,
  RowDragging,
  Selection,
  SortByGroupSummaryInfo,
  StateStoring,
  Summary,
  TotalItem,
} from 'devextreme-react/data-grid';
import { TextArea } from 'devextreme-react/text-area';
import activityIndicatorDark from 'images/loading-dark.gif';
import activityIndicatorLight from 'images/loading-light.gif';
import * as React from 'react';
import { useState, useRef } from 'react';
import { connect } from 'react-redux';
import { IconEnum } from 'util/enums/IconEnum';
import {
  ConvertDouble,
  formatNumberCustom,
  getTrimmedValue,
  localeDateString,
  setGridColumnAlignment,
  showCustomInformationMessage,
  showGridLayoutEdit,
  showUnsavedDataAlert,
} from 'util/helpers/helperFunctions';
import { addDummyColumn, fixBooleanColumns, fixColumnKeyName, generateLink, onEditorPreparing } from './GridFunctions';
import GridLoadingIcon from './Loading/GridLoadingIcon';
import DevXCalendar from './DevXCalendarControl/DevXCalendar';

const DEFAULT_WIDTH = '600px';
const CONTRACT_HEIGHT = '40px';
const PROPERTY = 'property';
const VALUE = 'value';
const DATA_TYPE_NAME = 'data_type_name';
const COLUMN_SIZE = 'column_size';
const NUMERIC_PRECISION = 'numeric_precision';
const NUMERIC_SCALE = 'numeric_scale';
const VERTICAL = 'Vertical';
const TABLE_CURRENCY = 'table_$$$';
const ROW_CURRENCY = 'row_$$$';
const ADD = 'Add';
const CREATE = 'Create';
const EDIT = 'Edit';
const DELETE = 'Delete';
const DELETE_ALL = 'Delete All';
const PASTE_FROM_CLIPBOARD = 'Paste from Clipboard';
const ADD_ICON_COLOR = '#0d83c9';
const EDIT_ICON_COLOR = '#0d83c9';
const DELETE_ICON_COLOR = '#f20000';
const PASTE_FROM_CLIPBOARD_ICON_COLOR = '#7DD358';
const DEFAULT_INSPECTOR_WIDTH = 470;
const DEFAULT_GRID_MARGIN = 10;

export const calcGridInspectorWidth = (inspectorWidth?: number, gridMargin?: number) => {
  const margin =
    (inspectorWidth ? inspectorWidth : DEFAULT_INSPECTOR_WIDTH) + (gridMargin ? gridMargin : DEFAULT_GRID_MARGIN);
  return `calc(100vw - ${margin}px)`;
};

export interface ICustomIcon {
  color?: string;
  onClick: (data: any, event: any) => void;
  iconType: IconEnum;
  tooltip: string;
  hidden?: any;
}

export interface IHeaderButton {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  icon?: any;
}

export interface ILinkedColumn {
  columnName: string;
  onClick: (event: any, data: any) => void;
  condition?: any;
}

export interface IGridEditParams {
  controllerName: string;
  defaultParams?: any;
  methodName: string;
  columnToDatabaseList: IColumnToDataBase[];
  onEditSuccess?: () => void;
  customCheck?: (data: any) => ICustomCheckResult | undefined;
  fetchRowData?: IFetchRowDataParams;
  multiLineColumnsList?: string[];
}

interface ICustomCheckResult {
  errorText: string;
  hasError: boolean;
}

export interface IFetchRowDataParams {
  fetchDataPromise: any;
  responseToDatabaseList: IColumnToDataBase[];
}

interface IColumnToDataBase {
  key: string;
  value: string;
  columnName?: string;
  isRequired?: boolean;
  isBoolean?: boolean;
}

export enum Theme {
  Dark,
  Light,
}

export enum DataFormat {
  money = 'money',
  smalldatetime = 'smalldatetime',
  boolean = 'boolean',
  percent = 'percent',
  dateandtime = 'dateandtime',
}

export interface ICustomCell {
  columnName: string;
  showCustomCell: (rowInfo: any) => boolean;
  cellStyle: any;
}

export interface ICustomCellRenderKeyProps {
  [key: string]: { cellRender: (props: any) => React.ReactNode };
}

export interface IProps {
  loadingMessage?: string;
  showGrouping?: boolean;
  showGroupPanel?: boolean;
  extraColumnProps?: IExtraColumnProps;
  toolbarIcon?: any;
  onRowPrepared?: any;
  onRowClick?: any;
  iconColumnHeader?: string;
  showMultiselect?: boolean;
  editParams?: IGridEditParams;
  hasColSpan?: boolean;
  columnsInfo?: IColumnProps[];
  customIcons?: ICustomIcon[];
  pageSizeOptions?: number[];
  defaultPageSize?: number;
  dataObject: IDataObject;
  isLoading: boolean;
  displayType?: string;
  showPager?: boolean;
  onEdit?: (data: any) => void;
  onDelete?: (data: any) => void;
  headerCustomButtons?: IHeaderButton[];
  customCellRenderKeyProps?: ICustomCellRenderKeyProps;
  linkedColumns?: ILinkedColumn[];
  customCells?: ICustomCell[];
  gridTitle?: string;
  enableEditableButton?: boolean;
  dxDataGridProps?: any;
  children?: any;
  showPagination?: boolean;
  showFilter?: boolean;
  showSearch?: boolean;
  disableAutoPagination?: boolean;
  width?: string;
  gridHeight?: string;
  gridContentHeight?: string;
  gridFixedHeight?: string;
  totalColumnName?: string;
  hideDelete?: any;
  hideEdit?: any;
  gridWidth?: string;
  allowAdd: boolean;
  allowDelete: boolean;
  allowEdit: boolean;
  theme?: Theme;
  columnPrecision?: IColumnPrecision[];
  onContentReady?: any;
  storageKey?: string;
  onSelectionChanged?: (e: any) => void;
  keyExpr?: any;
  selectedRowKeys?: any;
  defaultSelectedRowKeys?: any;
  repaintComponent?: React.RefObject<any>;
  onReorder?: (e: any) => void;
  onCopy?: () => void;
  onPaste?: () => void;
  reference?: React.RefObject<any>;
  loadPanelEnabled?: boolean;
  repaintChangesOnly?: boolean;
  onEditorPreparing?: (e: any) => void;
  singleSelect?: boolean;
  isLoadPanelDisabled?: boolean;
  heightToRemove?: number;
  allowResize?: boolean;
  userGroups: any;
  rowDraggingExtraProps?: any;
  showEditLayout?: boolean;
}

export interface IColumnProps {
  header: string;
  component?: any;
  dataType: DataType;
  dataField?: string;
  extraProps?: any;
  children?: IColumnProps[];
  dataFormat?: DataFormat;
  hasColSpan?: boolean;
}
interface IColumnPrecision {
  columnName: string;
  precision: number | 'nf';
}

export enum DataType {
  Date = 'date',
  Currency = 'currency',
  Number = 'number',
  DateTime = 'dateTime',
  String = 'string',
  Boolean = 'boolean',
}

export const defaultPageSizeOptions: number[] = [10, 15, 20, 25, 50, 100];

interface IState {
  dataFixed: boolean;
  isExpanded: boolean;
}

const GridControl: React.FC<IProps> = (props) => {
  const [dataFixed, setDataFixed] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  
  const updatingRef = useRef(false);
  const hasEditDataRef = useRef(false);
  const editButtonsHeaderRef = useRef<any[]>([]);
  const headerButtonsRef = useRef<any[]>([]);
  const totalRowDataRef = useRef<any>([]);
  const selectionAppliedRef = useRef(false);
  const prevDefaultSelectedRowKeysRef = useRef<any>(props.defaultSelectedRowKeys);
  
 if (!props.dataObject){ 
  return null;
 }
 
  let dataObject: IDataObject = props.dataObject;
  let enableEditableButton: boolean = props.enableEditableButton !== undefined ? props.enableEditableButton : true;
  let isLoading: boolean = props.isLoading;
  let displayType: string = props.displayType || 'Default';
  let pageSizeOptions: number[] = props.pageSizeOptions || defaultPageSizeOptions;
  let pageSizeDefault: number = props.defaultPageSize || 50;
  let defaultPageSize: number;
  let onEdit: ((data: any) => void) | undefined = props.onEdit;
  let onDelete: ((data: any) => void) | undefined = props.onDelete;
  let headerCustomButtons: IHeaderButton[] | undefined = props.headerCustomButtons;
  let linkedColumns: ILinkedColumn[] | undefined = props.linkedColumns;
  let showPagination: boolean = props.showPagination !== undefined ? props.showPagination : true;
  let showFilter: boolean = props.showFilter !== undefined ? props.showFilter : true;
  let showSearch: boolean = props.showSearch !== undefined ? props.showSearch : true;
  let showEditLayout: boolean = props.showEditLayout !== undefined ? props.showEditLayout : true;
  let showPager: boolean = props.showPager !== undefined ? props.showPager : true;
  let disableAutoPagination: boolean = props.disableAutoPagination !== undefined ? props.disableAutoPagination : false;
  let width: string = props.width || DEFAULT_WIDTH;
  let gridHeight: string = '';
  let gridWidth: string = props.gridWidth ? props.gridWidth : '100%';
  let gridContentHeight: string = props.gridContentHeight ? props.gridContentHeight : 'auto';
  let hiddenColumns: string[] = getHiddenColumns(dataObject);
  let columnPrecision: IColumnPrecision[] | undefined = props.columnPrecision;
  let storageKey: string | undefined = props.storageKey;
  let editParams: IGridEditParams | undefined = props.editParams;
  let extraColumnProps: any = { ...props.extraColumnProps };
  let customCellRenderKeyProps: ICustomCellRenderKeyProps | undefined = props.customCellRenderKeyProps;
  let isLoadPanelDisabled: boolean | undefined = props.singleSelect ? true : props.isLoadPanelDisabled;
  let heightToRemove: string | undefined;
  let allowResize: boolean | undefined = props.allowResize !== undefined ? props.allowResize : false;
  
  if (props.defaultPageSize !== undefined && props.defaultPageSize === -1) {
    defaultPageSize = dataObject.data.length;
  } else {
    defaultPageSize = props.defaultPageSize || pageSizeDefault;
  }
  
  // Reinitialize from props each render (original render() behavior)
  const removeHeight = props.heightToRemove !== undefined ? props.heightToRemove : 0;
  dataObject = props.dataObject;
  enableEditableButton = props.enableEditableButton !== undefined ? props.enableEditableButton : true;
  isLoading = props.isLoading;
  editParams = props.editParams;
  customCellRenderKeyProps = {
    ...customCellRenderKeyProps,
    ...props.customCellRenderKeyProps,
  };
  extraColumnProps = { ...props.extraColumnProps };
  displayType = props.displayType || 'Default';
  pageSizeOptions = props.pageSizeOptions || defaultPageSizeOptions;
  pageSizeDefault = props.defaultPageSize || 50;
  if (props.defaultPageSize !== undefined && props.defaultPageSize === -1) {
    defaultPageSize = dataObject.data.length;
  } else {
    defaultPageSize = props.defaultPageSize || pageSizeDefault;
  }
  onEdit = props.onEdit;
  onDelete = props.onDelete;
  headerCustomButtons = props.headerCustomButtons;
  linkedColumns = props.linkedColumns;
  showPagination = props.showPagination !== undefined ? props.showPagination : true;
  showFilter = props.showFilter !== undefined ? props.showFilter : true;
  showSearch = props.showSearch !== undefined ? props.showSearch : true;
  disableAutoPagination =
    props.disableAutoPagination !== undefined ? props.disableAutoPagination : false;
  width = props.width || DEFAULT_WIDTH;
  heightToRemove = `${removeHeight + 10}px`;
  gridHeight = props.gridFixedHeight
    ? props.gridFixedHeight
    : props.gridHeight
    ? `calc(100vh - ${props.gridHeight})`
    : `calc(100vh - ${heightToRemove})`;
  gridWidth = props.gridWidth ? props.gridWidth : '100%';
  hiddenColumns = getHiddenColumns(dataObject);
  gridContentHeight = props.gridContentHeight ? props.gridContentHeight : 'auto';
  storageKey = storageKey || getStorageKey(dataObject, displayType, props.gridTitle);

  const setExtraColumnPropsForLookUpField = () => {
    const extraColumnPropsLocal: any = {};
    const customCellRenderKeyPropsLocal: any = {};
    if (dataObject.lookupDataList) {
      dataObject.lookupDataList.forEach((element: any) => {
        const lookupList = element.LookupList;
        extraColumnPropsLocal[getTrimmedValue(element.ColumnName)] = {
          lookup: {
            allowClearing: true,
            dataSource: lookupList,
            displayExpr: 'lookup_code_desc',
            valueExpr: 'lookup_code',
            calculateCellValue: (value: any) => {
              if (value == null || !lookupList) return value;
              const found = lookupList.find((l: any) => l.lookup_code === value);
              return found ? found.lookup_code_desc : value;
            },
          },
        };
        customCellRenderKeyPropsLocal[getTrimmedValue(element.ColumnName)] = {
          cellRender: (cellData: any) => generateLink(cellData, props.linkedColumns),
        };
      });
      extraColumnProps = { ...extraColumnPropsLocal, ...extraColumnProps };
      customCellRenderKeyProps = {
        ...customCellRenderKeyPropsLocal,
        ...customCellRenderKeyProps,
      };
    }
  };
  
  const setGridContentHeight = (gridContentHeightParam: any) => {
    const root = document.documentElement;
    if (gridContentHeightParam !== undefined) {
      root.style.setProperty('--gridContentHeight', gridContentHeightParam);
    }
  };
  
  const setAndDeleteTotalCol = (dataObjectParam: any) => {
    const totalColumnName = getTrimmedValue(props.totalColumnName);
    const isColumnExist = dataObjectParam.schema.filter((schema: any) => schema.ColumnName === totalColumnName)[0];
    if (totalColumnName && isColumnExist) {
      dataObjectParam.data = dataObjectParam.data.filter((item: any) => {
        if (
          props.totalColumnName &&
          (getTrimmedValue(item[totalColumnName]) === '' || getTrimmedValue(item[totalColumnName]) === '0')
        ) {
          totalRowDataRef.current.push(item);
          return false;
        } else {
          return true;
        }
      });
    }
  };
  
  const setExtraColumnPropsForColumnOrder = () => {
    dataObject.schema.forEach((schemaObj: any) => {
      if (getTrimmedValue(schemaObj.ColumnOrder) !== '') {
        extraColumnProps[schemaObj.ColumnName] = {
          ...extraColumnProps[schemaObj.ColumnName],
          visibleIndex: schemaObj.ColumnOrder,
        };
      }
    });
  };
  
  const setMultiLineColEditCell = () => {
    if (editParams && editParams.multiLineColumnsList) {
      editParams.multiLineColumnsList.forEach((columnName: string) => {
        extraColumnProps[columnName] = {
          ...extraColumnProps[columnName],
          editCellComponent: textAreaEditCellComponent,
        };
      });
    }
  };

  const dateColComponent = (event: any) => {
    if (event) {
      return (
        <DevXCalendar
          value={event.data.value ? event.data.value : undefined}
          onChange={(value: any) => {
            event.data.setValue(value);
          }}
          style={{ width: '120px' }}
          isRequired={true}
          hideValidationControl={true}
          theme={Theme.Light}
        />
      );
    }
  };
  
  const setDateEdit = () => {
    if (editParams && editParams.columnToDatabaseList) {
      dataObject.schema.forEach((schemaObj: any) => {
        const dataTypeName = schemaObj.DataTypeName.toLowerCase();
        if (dataTypeName === 'smalldatetime' || dataTypeName === 'datetime') {
          extraColumnProps[schemaObj.ColumnName] = {
            ...extraColumnProps[schemaObj.ColumnName],
            editCellComponent: dateColComponent,
          };
          schemaObj.Width = 130;
        }
      });
    }
  };

  setExtraColumnPropsForLookUpField();
  setGridContentHeight(gridContentHeight);
  setAndDeleteTotalCol(dataObject);
  setExtraColumnPropsForColumnOrder();
  setMultiLineColEditCell();
  setDateEdit();

  const getColumns = (hasColSpan: boolean = false, columnInfo: IColumnProps[]) => {
    if (hasColSpan) {
      return columnInfo.map((col: IColumnProps, index: any) => {
        return (
          <Column
            key={index}
            caption={col.header}
            allowFiltering={false}
            alignment={'center'}
            dataField={col.dataField}
            dataType={col.dataType}
            cellRender={col.component}
            {...col.extraProps}
          >
            {col.children && getColumns(col.hasColSpan, col.children)}
          </Column>
        );
      });
    } else {
      return columnInfo ? (
        columnInfo.map((col: IColumnProps, index: any) => {
          return (
            <Column
              key={index}
              caption={col.header}
              allowFiltering={true}
              dataField={col.dataField}
              dataType={col.dataType}
              cellRender={(cellData: any) => getFormattedValue(col, cellData)}
              {...col.extraProps}
            />
          );
        })
      ) : (
        <></>
      );
    }
  };

  const getRadioButtons = (cellData: any) => {
    return (
      <input
        type="radio"
        id={'radioId'}
        name="selectGrid"
        checked={
          props.selectedRowKeys.length > 0
            ? props.selectedRowKeys[0] === cellData.data[props.keyExpr]
            : false
        }
        value={cellData.data[props.keyExpr]}
        onClick={undefined}
      />
    );
  };

  const calculateTotalRow = (options: any) => {
    if (options) {
      const optionRow = options.name.split(';');
      if (optionRow.length === 2) {
        const name = optionRow[0];
        const index = Number(optionRow[1]);
        options.totalValue =
          name === props.totalColumnName
            ? 'Total:'
            : getTrimmedValue(totalRowDataRef.current[index][name]) !== '' &&
              getTrimmedValue(totalRowDataRef.current[index][name]).toLowerCase() !== 'null'
            ? totalRowDataRef.current[index][name]
            : '';
      }
    }
  };

  const onSaveRowData = (event: any) => {
    let errorCol: any = [];
    if (editParams) {
      const rowData = { ...event.oldData, ...event.newData };
      let customCheckResult: ICustomCheckResult = {
        hasError: false,
        errorText: '',
      };
      editParams.columnToDatabaseList.forEach((data: IColumnToDataBase) => {
        if (data.isRequired && getTrimmedValue(rowData[data.key]) === '') {
          errorCol = [...errorCol, data.columnName ? data.columnName : data.key];
        }
      });
      if (editParams.customCheck != null) {
        const customCheckResultTemp = editParams.customCheck(rowData);
        if (customCheckResultTemp && customCheckResultTemp.hasError) {
          customCheckResult = customCheckResultTemp;
        }
      }
      if (errorCol.length > 0) {
        event.isValid = false;
        event.errorText =
          errorCol.length > 1
            ? 'Values for ' + errorCol.join(' , ') + ' are required.'
            : 'Value for ' + errorCol[0] + ' is required. ';
      } else if (customCheckResult.hasError) {
        event.isValid = false;
        event.errorText = customCheckResult.errorText;
      } else {
        let saveParam = { ...editParams.defaultParams };
        editParams.columnToDatabaseList.forEach((data: IColumnToDataBase) => {
          saveParam = data.isBoolean
            ? {
                ...saveParam,
                [data.value]: rowData[data.key] === 1 || rowData[data.key] === true ? 1 : 0,
              }
            : { ...saveParam, [data.value]: rowData[data.key] };
        });
        if (!updatingRef.current) {
          updatingRef.current = true;
        }
        const result = saveRowData(saveParam, rowData);
        event.promise = result.then((data: any) => {
          if (data && data.hasError) {
            event.isValid = false;
            event.errorText = data.errorMessage;
          }
        });
      }
    }
  };

  const saveRowData = async (saveParam: any, rowData: any) => {
    if (editParams) {
      if (editParams.fetchRowData) {
        const response = await editParams.fetchRowData.fetchDataPromise(rowData);
        if (response.dataObject.data.length > 0) {
          editParams.fetchRowData.responseToDatabaseList.forEach((data: IColumnToDataBase) => {
            saveParam = data.isBoolean
              ? {
                  ...saveParam,
                  [data.value]:
                    response.dataObject.data[0][data.key] === 1 || response.dataObject.data[0][data.key] === true
                      ? 1
                      : 0,
                }
              : {
                  ...saveParam,
                  [data.value]: response.dataObject.data[0][data.key],
                };
          });
        }
      }
      const result = await saveData(editParams.controllerName, editParams.methodName, saveParam, false);
      return result;
    }
  };

  const getToolItems = (columnName: string, dataType: string, index: number) => {
    return (
      <TotalItem
        cssClass={'dx-total-row'}
        key={`${columnName}${index}`}
        name={`${columnName};${index}`}
        summaryType="custom"
        displayFormat={'{0}'}
        showInColumn={columnName}
        valueFormat={
          dataType !== 'money'
            ? 'Number'
            : {
                precision: 2,
                type: 'fixedPoint',
              }
        }
        alignment={getTotalColumnAlignment(dataType)}
      />
    );
  };

  const getTotalColumnAlignment = (dataType: any) => {
    return getTrimmedValue(dataType).toLowerCase() === 'string' ||
      getTrimmedValue(dataType).toLowerCase() === 'char' ||
      getTrimmedValue(dataType).toLowerCase() === 'varchar'
      ? 'left'
      : 'right';
  };

  const getHeader = (col: any) => {
    if (col.headerComponent) {
      return col.headerComponent();
    } else {
      return <span>{col.header}</span>;
    }
  };

  const getFormattedValue = (col: any, cellData: any) => {
    if (col.component) {
      return col.component(cellData);
    } else {
      const cellValue = cellData.data[col.dataField];
      let formattedValue: any = cellValue;
      switch (col.dataFormat && col.dataFormat.toLowerCase()) {
        case 'tinyint':
        case 'boolean':
        case 'bit':
          formattedValue = 'No';
          if (cellValue === 1 || cellValue === true) {
            formattedValue = 'Yes';
          } else if (cellValue === 0 || cellValue === false) {
            formattedValue = 'No';
          } else {
            formattedValue = cellValue;
          }
          break;
        case 'datetime':
        case 'smalldatetime':
          // Rely on browser for date formatting
          const date = Date.parse(cellValue);
          formattedValue = !isNaN(date) ? new Date(cellValue).toLocaleDateString() : cellValue;
          break;
        case 'dateandtime':
          const datetime = Date.parse(cellValue);
          formattedValue = !isNaN(datetime) ? new Date(cellValue).toLocaleString() : cellValue;
          break;
        case 'money':
          formattedValue = formatNumberCustom(cellValue, 2);
          break;
        case 'percent':
          formattedValue = isNaN(cellValue) ? `${cellValue.toLocaleString()}%` : `${percentFormat(cellValue)}%`;
          break;
        default:
          formattedValue = cellValue;
          break;
      }
      return <span>{formattedValue}</span>;
    }
  };

  const checkHasEditData = () => {
    if (hasEditDataRef.current) {
      showUnsavedDataAlert('');
      return true;
    }
    return false;
  };

  const resetGrid = () => {
    resetStore(storageKey);
    // forceUpdate is not needed in functional components, React will re-render when state/props change
  };

  if (isLoading) {
    if (dataFixed) {
      setDataFixed(false);
    }
    return showLoading(
      displayType,
      showPagination,
      gridHeight,
      gridWidth,
      width,
      gridContentHeight,
      getTrimmedValue(props.loadingMessage),
      isExpanded,
      allowResize || false,
      props.theme,
    );
  } else if (displayType === VERTICAL) {
    return (
      <div
        style={{
          color: 'white',
          width: props.width || 'min-content',
          minWidth: width,
        }}
      >
        {getDataGrid(
          dataObject,
          displayType,
          pageSizeOptions,
          defaultPageSize,
          false,
          false,
          hiddenColumns,
          onEdit,
          onDelete,
          headerCustomButtons,
          linkedColumns,
        )}
      </div>
    );
  } else {
    if (!disableAutoPagination && !showPagination && dataObject.data.length > 20) {
      showPagination = true;
      defaultPageSize = 20;
      if (gridHeight === `calc(100vh - ${heightToRemove})`) {
        gridHeight = '50vh';
      }
    }
    return (
      <div
        style={getGridStyle(
          showPagination,
          isExpanded,
          allowResize || false,
          gridHeight,
          gridWidth,
        )}
      >
        {getDataGrid(
          dataObject,
          displayType,
          pageSizeOptions,
          defaultPageSize,
          enableEditableButton,
          showPagination,
          hiddenColumns,
          onEdit,
          onDelete,
          headerCustomButtons,
          linkedColumns,
        )}
      </div>
    );
  }

  function getDataGrid(
    dataObject: IDataObject,
    displayType: string,
    pageSizeOptions: number[],
    defaultPageSize: number,
    enableEditableButton: boolean,
    showPagination: boolean,
    hiddenColumns: string[],
    onEdit?: (data: any) => void,
    onDelete?: (data: any) => void,
    headerCustomButtons?: IHeaderButton[],
    linkedColumns?: ILinkedColumn[],
  ) {
    const gridDataObject: IDataObject = {
      data: [],
      schema: dataObject.schema,
      schemaType: SchemaType.GENESIS,
    };
    let sdColumns: string[] = [];
    let pageSize: number = defaultPageSize;
    let dataFixedLocal = dataFixed;
    if (displayType === VERTICAL) {
      const dataRow = dataObject.data[0];
      if (dataRow !== undefined) {
        const tableCurrency = getTableCurrency(dataObject);
        const rowCurrency = getRowCurrency(dataObject);
        const columnWithCurrency = getColumnWithCurrency(dataObject);
        dataObject.schema.forEach((column: any) => {
          let dataValue = dataRow[column.ColumnName];
          let currencyColumn = tableCurrency ? tableCurrency : rowCurrency;
          if (column.ColumnName.indexOf('_') === -1 && !hiddenColumns.includes(column.ColumnName.toString())) {
            if (column.ColumnName !== VALUE) {
              if (column.ColumnName !== PROPERTY) {
                let adjustedColumnName: string = column.ColumnName;
                const dataTypeName = column.DataTypeName.toLowerCase();
                if (column.Caption) {
                  adjustedColumnName = column.Caption;
                }
                const currencyCol: any = column.ColumnName + '_$$$';
                if (columnWithCurrency.indexOf(currencyCol) > -1) {
                  currencyColumn = getTrimmedValue(dataRow[currencyCol]);
                }
                if (dataTypeName === 'money' && currencyColumn !== '') {
                  adjustedColumnName = adjustedColumnName + ' (' + currencyColumn + ')';
                  // column name is adjusted therefore also update it in linkedColumns
                  if (linkedColumns) {
                    const idx = linkedColumns.findIndex(item => item.columnName === column.ColumnName);
                    if (idx !== -1) {
                      linkedColumns[idx].columnName = adjustedColumnName;
                    }
                  }
                } else if (dataTypeName === 'float') {
                  if (columnPrecision) {
                    for (const col of columnPrecision) {
                      if (col.precision !== 'nf' && column.ColumnName === col.columnName) {
                        if (!isNaN(dataValue)) {
                          dataValue =
                            dataValue !== undefined && dataValue !== null
                              ? ConvertDouble(dataValue).toFixed(col.precision)
                              : dataValue;
                        }
                        break;
                      }
                    }
                  }
                } else if (dataTypeName === 'bit' || dataTypeName === 'tinyint') {
                  dataValue = getTrimmedValue(dataValue) === '1' || dataValue === true ? 'Yes' : 'No';
                } else if (
                  dataTypeName.toLowerCase() === 'smalldatetime' ||
                  dataTypeName.toLowerCase() === 'datetime'
                ) {
                  // Format the date for datetime and smalldatetime data type in vertical grid
                  dataValue = localeDateString(dataValue);
                }

                gridDataObject.data.push({
                  ...dataRow,
                  column_size: column.ColumnSize,
                  data_type_name: column.DataTypeName,
                  format: column.Format,
                  numeric_precision: column.NumericPrecision,
                  numeric_scale: column.NumericScale,
                  property: adjustedColumnName,
                  value: dataValue,
                });
              }
            }
          }
        });

        let columnSchemaPresent: boolean = false;
        gridDataObject.schema.forEach((column: any) => {
          if (column.ColumnName === PROPERTY) {
            columnSchemaPresent = true;
          } else if (column.ColumnName !== VALUE) {
            column.Hidden = true;
          }
        });

        if (!columnSchemaPresent) {
          gridDataObject.schema.push({
            BaseColumnName: PROPERTY,
            ColumnName: PROPERTY,
            ColumnSize: 270,
            DataTypeName: 'varchar',
            NumericPrecision: 255,
            NumericScale: 255,
            Width: 270,
          });
          gridDataObject.schema.push({
            BaseColumnName: VALUE,
            ColumnName: VALUE,
            DataTypeName: 'varchar',
            ColumnSize: 400,
            NumericPrecision: 255,
            NumericScale: 255,
          });
          gridDataObject.schema.push({
            BaseColumnName: DATA_TYPE_NAME,
            ColumnName: DATA_TYPE_NAME,
            ColumnSize: 250,
            DataTypeName: 'varchar',
            NumericPrecision: 255,
            NumericScale: 255,
          });
          gridDataObject.schema.push({
            BaseColumnName: COLUMN_SIZE,
            ColumnName: COLUMN_SIZE,
            DataTypeName: 'varchar',
            ColumnSize: 250,
            NumericPrecision: 255,
            NumericScale: 255,
          });
          gridDataObject.schema.push({
            BaseColumnName: NUMERIC_PRECISION,
            ColumnName: NUMERIC_PRECISION,
            ColumnSize: 250,
            DataTypeName: 'varchar',
            NumericPrecision: 255,
            NumericScale: 255,
          });
          gridDataObject.schema.push({
            BaseColumnName: NUMERIC_SCALE,
            ColumnName: NUMERIC_SCALE,
            ColumnSize: 250,
            DataTypeName: 'varchar',
            NumericPrecision: 255,
            NumericScale: 255,
          });
        }
        extraColumnProps = {
          ...extraColumnProps,
          value: { ...extraColumnProps.value, cssClass: 'wrapped-column' },
        };
        pageSize = gridDataObject.data.length;
      } else {
        pageSize = 1;
        // Handled condition for Vertical grid, where no data is available
        if (headerCustomButtons !== undefined && headerCustomButtons.length > 0) {
          headerCustomButtons.forEach(headerButton => {
            if (headerButton.label.trim() === DELETE || headerButton.label.trim() === EDIT) {
              headerButton.disabled = true;
            }
          });
        }
      }
    } else {
      if (dataObject.schema.length > 0) {
        fixPercentageColumns(dataObject, columnPrecision);
        sdColumns = fixSdColumns(dataObject);
      }
      if (dataObject.schema.length > 0 && !dataFixed) {
        fixBooleanColumns(dataObject);
        const tableCurrency: string = getTableCurrency(dataObject);
        const columnWithCurrency = getColumnWithCurrency(dataObject);
        const columnsToChange: IColumnPrecision[] = [];

        // moved code inside one loop
        dataObject.schema.forEach((col: any) => {
          // fix schema column names with period
          if (col.ColumnName.includes('.')) {
            if (col.Caption === '') {
              col.Caption = col.ColumnName;
            }
            col.ColumnName = fixColumnKeyName(col.ColumnName);
          }

          fixCurrencyColumn(col, tableCurrency, columnWithCurrency, dataObject.data, extraColumnProps);
          fixFloatColumns(col, columnsToChange, columnPrecision);
        });

        dataObject.data.forEach((data: any) => {
          // fix data key names with period
          const dataKeys = Object.keys(data);
          dataKeys.forEach((key: string) => {
            if (key.includes('.')) {
              const newKey = fixColumnKeyName(key);
              data[newKey] = data[key];
              delete data[key];
            }
          });

          fixCurrencyColumnData(data, columnWithCurrency);
          fixFloatColumnData(data, columnsToChange);
        });

        fixHiddenColumns(dataObject, hiddenColumns);
        addDummyColumn(dataObject);

        dataFixedLocal = true;

        setDataFixed(dataFixedLocal);
      } else if (props.columnsInfo) {
        dataFixedLocal = true;
      }

      gridDataObject.data = dataObject.data;
      if (!showPagination) {
        pageSize = Math.max(dataObject.data.length, 1);
        pageSizeOptions = [];
        pageSizeOptions.push(pageSize);
      }
    }

    const headerButtons: any = [];
    const editButtonsHeader: any = [];
    const editButtons: any = [];

    if (headerCustomButtons !== undefined) {
      headerCustomButtons.forEach(headerButton => {
        if (headerButton.label.trim() === ADD || headerButton.label.trim() === EDIT) {
          if (displayType === VERTICAL && headerButton.label.trim() === ADD && props.allowAdd) {
            headerButtons.push({
              location: 'before',
              icon: headerButton.label.toLowerCase(),
              label: '',
              hint: 'Add',
              onClick: headerButton.onClick,
            });
          } else if (displayType === VERTICAL && headerButton.label.trim() === EDIT && props.allowEdit) {
            headerButtons.push({
              location: 'before',
              icon: headerButton.label.toLowerCase(),
              label: '',
              hint: 'Edit',
              onClick: headerButton.onClick,
            });
          } else if (headerButton.label.trim() === ADD && props.allowAdd) {
            editButtonsHeader.push({
              color: ADD_ICON_COLOR,
              iconType: IconEnum.Add,
              onClick: () => {
                if (checkHasEditData()) {
                  return;
                }
                headerButton !== undefined ? headerButton.onClick() : voidFunction();
              },
              tooltip: 'Add',
            });
          }
        } else if (headerButton.label.trim() === DELETE && props.allowDelete) {
          headerButtons.push({
            location: 'before',
            icon: 'trash',
            label: '',
            hint: 'Delete',
            onClick: headerButton.onClick,
          });
        } else if (displayType !== VERTICAL && headerButton.label.trim() === DELETE_ALL && props.allowDelete) {
          if (dataObject.data.length > 0) {
            editButtonsHeader.push({
              color: DELETE_ICON_COLOR,
              iconType: IconEnum.Delete,
              onClick: headerButton.onClick,
              tooltip: 'Delete All',
            });
          }
        } else if (
          displayType !== VERTICAL &&
          headerButton.label.trim() === PASTE_FROM_CLIPBOARD &&
          props.allowAdd
        ) {
          editButtonsHeader.push({
            color: PASTE_FROM_CLIPBOARD_ICON_COLOR,
            iconType: IconEnum.PasteFromClipboard,
            onClick: headerButton.onClick,
            tooltip: 'Paste from Clipboard',
          });
        } else if (props.allowAdd) {
          const buttonIcon: string =
            headerButton.label.startsWith(ADD) || headerButton.label.startsWith(CREATE)
              ? 'add'
              : headerButton.icon != null
              ? headerButton.icon
              : 'preferences';
          headerButtons.push({
            location: 'after',
            icon: buttonIcon,
            label: headerButton.label,
            onClick: headerButton.onClick,
          });
        }
      });
    }
    
    // Update ref unconditionally so headerButtons onClick always resolves to the latest handler at click time.
    // DevExtreme captures the toolbar buttons once and never re-reads the prop, so without this ref the onClick closures would be stale
    headerButtonsRef.current = headerButtons;
    
    if (dataFixedLocal) {
      editButtonsHeaderRef.current = editButtonsHeader;
      if (onEdit !== undefined && props.allowEdit) {
        editButtons.push({
          color: EDIT_ICON_COLOR,
          hidden: (cellInfo: any) => (props.hideEdit ? props.hideEdit(cellInfo) : false),
          iconType: IconEnum.Edit,
          onClick: (cellInfo: any) => {
            if (checkHasEditData()) {
              return;
            }
            onEdit !== undefined ? onEdit(cellInfo) : voidFunction();
          },
          tooltip: 'Edit',
        });
      }
      if (onDelete !== undefined && props.allowDelete) {
        editButtons.push({
          color: DELETE_ICON_COLOR,
          hidden: (cellInfo: any) => (props.hideDelete ? props.hideDelete(cellInfo) : false),
          iconType: IconEnum.Delete,
          onClick: (cellInfo: any) => {
            if (checkHasEditData()) {
              return;
            }
            onDelete !== undefined ? onDelete(cellInfo) : voidFunction();
          },
          tooltip: 'Delete',
        });
      }
      if (props.customIcons && props.customIcons.length > 0) {
        props.customIcons.forEach((icon: ICustomIcon) => {
          editButtons.push({
            color: icon.color ? icon.color : '#0d83c9',
            hidden: (cellInfo: any) => (icon.hidden ? icon.hidden(cellInfo) : false),
            iconType: icon.iconType,
            onClick: (cellInfo: any, event: any) => {
              icon.onClick(cellInfo, event);
            },
            tooltip: icon.tooltip,
          });
        });
      }
    }
    enableEditableButton =
      props.enableEditableButton !== undefined
        ? enableEditableButton
        : displayType !== VERTICAL && editButtons.length > 0;
    const buttonColumnWidth: number = Math.max(editButtons.length * 24, editButtonsHeaderRef.current.length * 24, 28);
    const moreButtonActions: any = [];
    if (dataObject.sqlCommandText !== undefined) {
      moreButtonActions.push({
        label: 'View Source',
        icon: 'info',
        onClick: () => showCustomInformationMessage(dataObject.sqlCommandText || '', 'Source Information', true),
      });
      if (
        displayType !== VERTICAL &&
        props.userGroups.filter((group: any) => group.groupId === 9 || group.groupId === 1).length &&
        showEditLayout
      ) {
        moreButtonActions.push({
          icon: 'edit',
          label: 'Edit Layout',
          onClick: () =>
            showGridLayoutEdit({
              sqlCommandText: dataObject.sqlCommandText,
              schema: dataObject.schema,
              storageKey: storageKey,
            }),
        });
      }
    }

    if (pageSize === 0 && isLoading) {
      return showLoading(
        displayType,
        showPagination,
        gridHeight,
        gridHeight,
        width,
        gridContentHeight,
        getTrimmedValue(props.loadingMessage),
        isExpanded,
        allowResize || false,
        props.theme,
      );
    } else {
      if (showPagination) {
        const groupPanelCount = getGroupPanelCount(dataObject.data, props.extraColumnProps);
        pageSizeOptions = generatePageSizeOptions(pageSizeOptions, dataObject.data.length + groupPanelCount);
        if (pageSizeDefault === -1) {
          pageSize = pageSize + groupPanelCount;
        }
        if (pageSize > pageSizeOptions[pageSizeOptions.length - 1]) {
          pageSize = pageSizeOptions[pageSizeOptions.length - 1];
        }
      }
      setPageSize(pageSizeOptions, pageSize, storageKey, pageSizeDefault);
      removeSelectedRows(storageKey);
      let resetButton: any = {};

      if (storageKey) {
        resetButton = {
          location: 'after',
          options: {
            hint: 'Reset',
            icon: 'refresh',
            onClick: resetGrid,
          },
          widget: 'dxButton',
        };
      }

      const infoText: string = 'Page {0} of {1} ({2} record' + (dataObject.data.length > 1 ? 's)' : ')');
      const isButtonColumnVisible = { visible: false };
      const extraProps: any = {};
      if (props.showMultiselect) {
        if (props.keyExpr) {
          extraProps.keyExpr = props.keyExpr;
        }
        if (props.selectedRowKeys) {
          extraProps.selectedRowKeys = props.selectedRowKeys;
        }
      }
      let addBefore: any = [];
      if (allowResize && displayType !== VERTICAL) {
        addBefore = [
          ...addBefore,
          {
            location: 'before',
            options: {
              hint: isExpanded ? 'Hide' : 'Expand',
              icon: isExpanded ? 'chevronup' : 'chevrondown',
              onClick: () => setIsExpanded(!isExpanded),
            },
            widget: 'dxButton',
          },
        ];
      }
      if (props.toolbarIcon) {
        addBefore = [
          ...addBefore,
          {
            location: 'before',
            options: {
              ...props.toolbarIcon,
            },
            widget: 'dxButton',
          },
        ];
      }
      if (props.onCopy) {
        addBefore = [
          ...addBefore,
          {
            location: 'after',
            options: {
              hint: 'Copy',
              icon: 'copy',
              onClick: props.onCopy,
            },
            widget: 'dxButton',
          },
        ];
      }
      if (props.onPaste) {
        addBefore = [
          ...addBefore,
          {
            location: 'after',
            options: {
              hint: 'Paste',
              icon: 'paste',
              onClick: props.onPaste,
            },
            widget: 'dxButton',
          },
        ];
      }

      if (displayType !== VERTICAL) {
        gridDataObject.schema.forEach((column: any) => {
          if (column.DataTypeName === 'boolean') {
            column.DataTypeName = 'bool';
          }
        });
      }

      return (
        <DxDataGrid
          useDarkTheme={props.theme === Theme.Dark ? true : false}
          ref={props.reference}
          extraColumnProps={extraColumnProps}
          onSelectionChanged={props.onSelectionChanged}
          dataSource={gridDataObject}
          dataSourceType={DataSourceType.VIRTUS}
          displayType={displayType}
          repaintChangesOnly={props.repaintChangesOnly}
          dxDataGridComponentsProps={{ ColumnChooser: { search: { enabled: true } } }}
          dxDataGridProps={{
            columnResizingMode: 'widget',
            focusedRowEnabled: !props.showMultiselect,
            headerFilter: {},
            // onCellPrepared: props.totalColumnName != null ? (event: any) => formatTotalRow(event, getTrimmedValue(props.totalColumnName)) : undefined,
            onCellPrepared: sdColumns.length
              ? (event: any) => {
                  if (sdColumns.length) {
                    setGridColumnAlignment(event, sdColumns, 'right');
                  }
                }
              : undefined,
            onContentReady: (event: any) => {
              if (prevDefaultSelectedRowKeysRef.current !== props.defaultSelectedRowKeys) {
                prevDefaultSelectedRowKeysRef.current = props.defaultSelectedRowKeys;
                selectionAppliedRef.current = false;
              }
              if (!selectionAppliedRef.current && props.defaultSelectedRowKeys?.length > 0) {
                selectionAppliedRef.current = true;
                event.component.selectRows(props.defaultSelectedRowKeys, false);
              }
              if (props.repaintComponent && props.repaintComponent.current) {
                (props.repaintComponent as any).current?.instance?.repaint?.();
              }
              hasEditDataRef.current = event.component.hasEditData();
              if (updatingRef.current && !hasEditDataRef.current) {
                updatingRef.current = false;
                if (editParams && editParams.onEditSuccess) {
                  editParams.onEditSuccess();
                }
              }
              if (props.onContentReady) {
                props.onContentReady(event);
              }
            },
            onEditorPreparing: props.onEditorPreparing || onEditorPreparing,
            onRowClick: props.onRowClick,
            onRowPrepared: props.onRowPrepared,
            onRowValidating: onSaveRowData,
            toolbarButtons: {
              addAfter: [resetButton],
              addBefore,
            },
            remove: [],

            ...extraProps,
            ...(({ defaultSelectedRowKeys: _ignored, ...rest }) => rest)(props.dxDataGridProps || {}),
          }}
          indicatorSrc={props.theme === Theme.Dark ? activityIndicatorLight : activityIndicatorDark}
          loadPanelEnabled={!isLoadPanelDisabled}
          showColumnLines={true}
          showRowLines={true}
          showColumnHeaders={displayType === VERTICAL ? false : true}
          showPaging={showPagination}
          showGrouping={false}
          showSearch={displayType === VERTICAL ? false : showSearch}
          showFilter={displayType === VERTICAL ? false : showFilter}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          showRefresh={false /*(displayType === VERTICAL) ? false : true*/}
          showColumnChooser={false}
          gridTitle={props.gridTitle}
          disableStateStoring={true}
          exportFileName={'GenesisExport'}
          customCellRenderKeyProps={customCellRenderKeyProps}
          linkedColumns={linkedColumns}
          headerButtons={headerButtons.map((btn: any, index: number) => ({
            ...btn,
            onClick: () => headerButtonsRef.current[index]?.onClick(),
          }))}
          moreButtonActions={moreButtonActions}
          storageKey={''}
        >
          {props.showGrouping && <Grouping autoExpandAll={true} />}
          {props.showGroupPanel && <GroupPanel visible={true} />}
          {editParams && props.allowEdit && (
            <Editing mode="batch" startEditAction={'dblClick'} selectTextOnEditStart={true} allowUpdating={true} />
          )}
          <StateStoring
            enabled={true}
            storageKey={storageKey}
            type={'custom'}
            customSave={(state: any) => customSave(storageKey, state, dataObject.sqlCommandText)}
            customLoad={() => customLoad(storageKey, dataObject.sqlCommandText)}
          />
          {displayType !== VERTICAL && (editButtons.length > 0 || editButtonsHeaderRef.current.length > 0) ? (
            <Column
              // cssClass={props.theme === Theme.Dark ? 'dx-column-buttons' : ''}
              name="buttonColumn"
              allowExporting={false}
              allowGrouping={false}
              allowReordering={false}
              allowSorting={false}
              fixed={true}
              fixedPosition="left"
              width={buttonColumnWidth}
              minWidth={35}
              cellRender={(cellData: any): any => generateButtons(cellData, editButtons, isButtonColumnVisible)}
              headerCellRender={() => generateHeaderButtons(editButtonsHeaderRef, props)}
            />
          ) : (
            <></>
          )}
          {props.hasColSpan && props.columnsInfo ? (
            props.columnsInfo.map((col: IColumnProps, index: any) => {
              return (
                <Column
                  key={index}
                  caption={col.header}
                  allowFiltering={false}
                  alignment={'center'}
                  dataField={col.dataField}
                  dataType={col.dataType}
                  cellRender={col.component}
                  {...col.extraProps}
                >
                  {/* Get Column wil Recursively call to get nexted columns */}
                  {props.columnsInfo ? col.children && getColumns(col.hasColSpan, col.children) : <></>}
                </Column>
              );
            })
          ) : props.columnsInfo ? (
            props.columnsInfo.map((col: IColumnProps, index: any) => {
              return (
                <Column
                  key={index}
                  caption={col.header}
                  allowFiltering={true}
                  dataField={col.dataField}
                  dataType={col.dataType}
                  cellRender={(cellData: any) => getFormattedValue(col, cellData)}
                  {...col.extraProps}
                />
              );
            })
          ) : (
            <></>
          )}
          {showPager && displayType !== VERTICAL && dataObject.data.length > 0 ? (
            <Pager
              allowedPageSizes={pageSizeOptions}
              showPageSizeSelector={pageSizeOptions.length > 1 ? true : false}
              showInfo={true}
              infoText={infoText}
              visible={true}
            />
          ) : (
            <></>
          )}

          <Selection
            mode="multiple"
            showCheckBoxesMode={props.showMultiselect ? 'always' : 'none'}
            allowSelectAll={true}
          />
          <Export enabled={true} allowExportSelectedData={true} />
          {props.onReorder ? (
            <RowDragging
              allowReordering={true}
              onReorder={(event: any) => {
                if (checkHasEditData()) {
                  return;
                }
                if (props.onReorder !== undefined) {
                  props.onReorder(event);
                }
              }}
              showDragIcons={false}
              {...props.rowDraggingExtraProps}
            />
          ) : (
            <></>
          )}
          <Summary calculateCustomSummary={calculateCustomSummary}>
            <GroupItem column="Test Name" summaryType="custom" name="custom-count" />
            <GroupItem column="Display Order" summaryType="min" name="DisplayOrder" />
          </Summary>
          {props.singleSelect && (
            <Column
              alignment="center"
              width="30px"
              caption=""
              fixedPosition="left"
              fixed={true}
              cellRender={getRadioButtons}
            />
          )}
          {getTrimmedValue(props.totalColumnName) !== '' && totalRowDataRef.current && totalRowDataRef.current.length > 0 && (
            <Summary calculateCustomSummary={calculateTotalRow} recalculateWhileEditing={true}>
              {props.dataObject.schema.length > 0
                ? totalRowDataRef.current.map((totalRow: any, index: any) => {
                    return (
                      props.dataObject.schema &&
                      props.dataObject.schema.map((column: any) => {
                        return getToolItems(column.ColumnName, column.DataTypeName, index);
                      })
                    );
                  })
                : props.hasColSpan && props.columnsInfo
                ? totalRowDataRef.current.map((totalRow: any, index: any) => {
                    return (
                      props.columnsInfo &&
                      props.columnsInfo.map(col => {
                        return (
                          col.children &&
                          col.children.map((child: any) => {
                            return getToolItems(child.dataField, child.DataFormat, index);
                          })
                        );
                      })
                    );
                  })
                : totalRowDataRef.current.map((totalRow: any, index: any) => {
                    return (
                      props.columnsInfo &&
                      props.columnsInfo.map((col: any) => {
                        return getToolItems(col.dataField, col.DataFormat, index);
                      })
                    );
                  })}
            </Summary>
          )}
          <SortByGroupSummaryInfo summaryItem="min" />
          {props.children}
        </DxDataGrid>
      );
    }
  }
};

const percentFormat = (value: number) => {
  if (!value) {
    return '0';
  }
  if (Number.isInteger(Number(value))) {
    return Number(value);
  }
  return Number(value).toFixed(3);
};

const getHiddenColumns = (dataObject: IDataObject) => {
  const hiddenColumns: string[] = [];
  if (dataObject.schema.find(item => item.ColumnName === 'field_header_properties')) {
    if (dataObject.data.length > 0) {
      let fieldHeaderProperties: string = dataObject.data[0]['field_header_properties'];

      if (fieldHeaderProperties.charAt(-1) === '|') {
        fieldHeaderProperties = fieldHeaderProperties.slice(0, -1);
      }
      const aFieldHeaderProperties: string[] = fieldHeaderProperties.split('|');
      aFieldHeaderProperties.forEach(headerProperty => {
        const aFieldHeader = headerProperty.split(';'); // string format is (column name;column caption;hidden)
        if (aFieldHeader.length === 3) {
          if (aFieldHeader[2] === '1') {
            // third item indicates column is hidden
            hiddenColumns.push(aFieldHeader[0]);
          } else if (aFieldHeader[2] === '0') {
            // if column is not hidden use the caption override
            const idx = dataObject.schema.findIndex(item => item.ColumnName === aFieldHeader[0]);
            if (idx !== -1) {
              dataObject.schema[idx].Caption = aFieldHeader[1];
            }
          }
        }
      });
    }
  }

  return hiddenColumns;
};

function getTableCurrency(dataObject: IDataObject): string {
  let tableCurrency: string = '';
  if (dataObject.data.length > 0) {
    tableCurrency = getTrimmedValue(dataObject.data[0].table_$$$);
  }
  return getTrimmedValue(tableCurrency);
}

function getRowCurrency(dataObject: IDataObject): string {
  let rowCurrency: string = '';
  if (dataObject.data.length > 0) {
    rowCurrency =
      getTrimmedValue(dataObject.data[0].row_$$$) !== '' ? dataObject.data[0].row_$$$ : dataObject.data[0].Row_$$$;
  }
  return getTrimmedValue(rowCurrency);
}

function getColumnWithCurrency(dataObject: IDataObject): string[] {
  const columnWithCurrency: string[] = [];

  dataObject.schema.forEach((column: any) => {
    if (column.ColumnName !== TABLE_CURRENCY && column.ColumnName.endsWith('_$$$')) {
      const parentColumn = column.ColumnName.replace('_$$$', '');
      if (dataObject.schema.find(item => item.ColumnName === parentColumn)) {
        columnWithCurrency.push(column.ColumnName);
      }
    }
  });

  return columnWithCurrency;
}

function getGridStyle(
  showPagination: boolean,
  isExpanded: boolean,
  allowResize: boolean,
  gridHeight: string,
  gridWidth: string,
): any {
  let gridStyle = {
    color: 'white',
    display: 'flex',
    width: gridWidth,
    height: '100%',
  };
  if (showPagination === true) {
    gridStyle = { ...gridStyle, height: gridHeight };
  }
  if (allowResize && !isExpanded) {
    gridStyle = {
      ...gridStyle,
      height: CONTRACT_HEIGHT,
    };
  }
  return gridStyle;
}

function getButtonStyle(buttonColor: any): any {
  return {
    color: buttonColor,
    height: '16px',
    width: '16px',
  };
}

function getHeaderButtonStyle(buttonColor: any): any {
  return {
    color: buttonColor,
    height: '16px',
    width: '16px',
  };
}

function voidFunction() {
  return false;
}

function showLoading(
  displayType: string,
  showPagination: boolean,
  gridHeight: string,
  gridWidth: string,
  vGridWidth: string,
  gridContentHeight: any,
  loadingMessage: string,
  isExpanded: boolean,
  allowResize: boolean,
  theme?: Theme,
): any {
  const showActivityIndicator = () => {
    return (
      <GridLoadingIcon
        showLoadingMessage={loadingMessage ? true : false}
        loadingMessage={loadingMessage}
        theme={theme}
      />
    );
  };
  let divStyle: any = {
    alignItems: 'center',
    backgroundColor: theme === Theme.Dark ? '#242f39' : 'white',
    border: '1px solid',
    borderTop: '4px solid var(--default-blue)',
    display: 'block',
    height: 'auto',
    justifyContent: 'center',
    margin: '0px',
    padding: '20px',
    textAlign: 'center',
    verticalAlign: 'middle',
    width: 'auto',
  };

  if (displayType === VERTICAL) {
    divStyle = { ...divStyle, width: vGridWidth };

    return <div style={divStyle}>{showActivityIndicator()}</div>;
  } else {
    divStyle = { ...divStyle, display: 'flex', height: '100%', width: '100%' };

    if (gridContentHeight !== undefined) {
      divStyle = {
        ...divStyle,
        height: gridContentHeight,
      };
    }

    if (showPagination === true) {
      divStyle = { ...divStyle, height: gridHeight, width: gridWidth };
    }

    if (allowResize && !isExpanded) {
      divStyle = {
        ...divStyle,
        height: CONTRACT_HEIGHT,
      };
    }

    return <div style={divStyle}>{showActivityIndicator()}</div>;
  }
}

function fixHiddenColumns(dataObject: IDataObject, hiddenColumns: string[]) {
  hiddenColumns.forEach(col => {
    const idx = dataObject.schema.findIndex(item => item.ColumnName === col);

    if (idx !== -1) {
      dataObject.schema[idx].Hidden = true;
    }
  });
}

function fixCurrencyColumn(col: any, tableCurrency: any, columnWithCurrency: any, data: any, extraColumnProps: any) {
  let hasCurrencyColumn: boolean = false;
  if (tableCurrency !== '' && col.DataTypeName.toLowerCase() === 'money') {
    hasCurrencyColumn = false;
    columnWithCurrency.forEach((column: string) => {
      if (column.replace('_$$$', '') === col.ColumnName) {
        hasCurrencyColumn = true;
      }
    });
    if (!hasCurrencyColumn && col.ColumnName !== tableCurrency) {
      col.Caption = col.ColumnName + ' (' + tableCurrency + ')';
    }
  } else if (col.ColumnName.endsWith('_$$$') && col.ColumnName !== TABLE_CURRENCY) {
    hasCurrencyColumn = false;
    columnWithCurrency.forEach((column: string) => {
      if (column === col.ColumnName) {
        hasCurrencyColumn = true;
      }
    });
    if (col.ColumnName === ROW_CURRENCY) {
      hasCurrencyColumn = true;
    }
    if (hasCurrencyColumn) {
      const result = data.length > 0 ? (getTrimmedValue(data[0][col.ColumnName]) === '' ? false : true) : true;

      if (result) {
        col.Hidden = false;
        col.Caption = col.ColumnName === ROW_CURRENCY ? 'Currency Code' : col.ColumnName.replace('_$$$', ' Currency');
        col.Width = 120;
      }
    }
  }
  if (hasCurrencyColumn) {
    extraColumnProps[col.ColumnName] = {
      ...extraColumnProps[col.ColumnName],
      allowEditing: false,
    };
  }
}

function fixCurrencyColumnData(data: any, columnWithCurrency: any) {
  for (const column of columnWithCurrency) {
    if (getTrimmedValue(data[column.replace('_$$$', '')]) === '') {
      data[column] = '';
    }
  }
}

function fixFloatColumns(col: any, columnsToChange: IColumnPrecision[], columnPrecision?: IColumnPrecision[]) {
  if (col.DataTypeName === 'float') {
    if (col.Format === undefined) {
      if (columnPrecision) {
        for (const column of columnPrecision) {
          if (column.precision !== 'nf' && column.columnName === col.ColumnName) {
            columnsToChange.push({
              columnName: column.columnName,
              precision: column.precision,
            });
          }
        }
      }
    }
  }
}

function fixFloatColumnData(data: any, columnsToChange: IColumnPrecision[]) {
  columnsToChange.forEach((column: IColumnPrecision) => {
    if (column.precision && column.precision !== 'nf') {
      data[column.columnName] =
        data[column.columnName] !== undefined && data[column.columnName] === null
          ? ConvertDouble(data[column.columnName]).toFixed(column.precision)
          : data[column.columnName];
    }
  });
}

function fixPercentageColumns(dataObject: IDataObject, columnPrecision?: IColumnPrecision[]) {
  dataObject.schema.forEach(col => {
    if (col.ColumnName.endsWith('%')) {
      if (col.Format === undefined) {
        col.Format = 'nf'; // special code for no formatting
        if (columnPrecision) {
          for (const column of columnPrecision) {
            if (column.columnName === col.ColumnName) {
              col.Format = column.precision.toString();
              break;
            }
          }
        }
      }
    }
  });
}

function fixSdColumns(dataObject: IDataObject) {
  const sdColumns: string[] = [];
  dataObject.schema.forEach(col => {
    if (col.Format === 'sd') {
      // col.Format = undefined;
      sdColumns.push(col.ColumnName);
    }
  });
  return sdColumns;
}

function generateButtons(cellData: any, editButtons: any[], isButtonColumnVisible: any): any {
  const headerStyle: any = {
    alignItems: 'center',
    display: 'block',
    height: 'auto',
    justifyContent: 'center',
    textAlign: 'left',
    verticalAlign: 'middle',
    width: 'auto',
  };
  const rowData = { original: cellData.data, row: cellData.data };
  const cellInfo = { row: cellData.data };
  return (
    <span style={headerStyle}>
      {' '}
      {editButtons.map((button: any) => {
        if (button.hidden(cellInfo) === false) {
          isButtonColumnVisible.visible = true;
          switch (button.iconType) {
            case IconEnum.Delete:
              return (
                <a
                  key={'aD' + rowData.original._id_}
                  href="#self"
                  onClick={(e: any) => {
                    e.preventDefault();
                    button.onClick(rowData, e);
                  }}
                  title={button.tooltip}
                >
                  <Delete
                    key={'D' + rowData.original._id_}
                    data-testid={'D' + rowData.original._id_}
                    style={getButtonStyle(button.color)}
                  />
                </a>
              );
            case IconEnum.Add:
              return (
                <a
                  key={'aA' + rowData.original._id_}
                  href="#self"
                  onClick={(e: any) => {
                    e.preventDefault();
                    button.onClick(rowData, e);
                  }}
                  title={button.tooltip}
                >
                  <Add
                    key={'D' + rowData.original._id_}
                    data-testid={'A' + rowData.original._id_}
                    style={getButtonStyle(button.color)}
                  />
                </a>
              );
            case IconEnum.Properties:
              return (
                <a
                  key={'aP' + rowData.original._id_}
                  href="#self"
                  onClick={(e: any) => {
                    e.preventDefault();
                    button.onClick(rowData, e);
                  }}
                  title={button.tooltip}
                >
                  <Properties
                    key={'P' + rowData.original._id_}
                    data-testid={'P' + rowData.original._id_}
                    style={getButtonStyle(button.color)}
                  />
                </a>
              );
            case IconEnum.Transactions:
              return (
                <a
                  key={'aT' + rowData.original._id_}
                  href="#self"
                  onClick={(e: any) => {
                    e.preventDefault();
                    button.onClick(rowData, e);
                  }}
                  title={button.tooltip}
                >
                  <Transactions
                    key={'T' + rowData.original._id_}
                    data-testid={'T' + rowData.original._id_}
                    style={getButtonStyle(button.color)}
                  />
                </a>
              );
            case IconEnum.Folder:
              return (
                <a
                  key={'aF' + rowData.original._id_}
                  href="#self"
                  onClick={(e: any) => {
                    e.preventDefault();
                    button.onClick(rowData, e);
                  }}
                  title={button.tooltip}
                >
                  <FolderOpen
                    key={'T' + rowData.original._id_}
                    data-testid={'F' + rowData.original._id_}
                    style={getButtonStyle(button.color)}
                  />
                </a>
              );
            case IconEnum.Clear:
              return (
                <a
                  key={'aC' + rowData.original._id_}
                  href="#self"
                  onClick={(e: any) => {
                    e.preventDefault();
                    button.onClick(rowData, e);
                  }}
                  title={button.tooltip}
                >
                  <Clear
                    key={'T' + rowData.original._id_}
                    data-testid={'C' + rowData.original._id_}
                    style={getButtonStyle(button.color)}
                  />
                </a>
              );
            case IconEnum.Unlink:
              return (
                <a
                  key={'aU' + rowData.original._id_}
                  href="#self"
                  onClick={(e: any) => {
                    e.preventDefault();
                    button.onClick(rowData, e);
                  }}
                  title={button.tooltip}
                >
                  <LinkOffIcon
                    key={'U' + rowData.original._id_}
                    data-testid={'U' + rowData.original._id_}
                    style={getButtonStyle(button.color)}
                  />
                </a>
              );
            case IconEnum.Pause:
              return (
                <a
                  key={'aP' + rowData.original._id_}
                  href="#self"
                  onClick={(e: any) => {
                    e.preventDefault();
                    button.onClick(rowData, e);
                  }}
                  title={button.tooltip}
                >
                  <PauseLogo
                    key={'P' + rowData.original._id_}
                    data-testid={'P' + rowData.original._id_}
                    style={getButtonStyle(button.color)}
                  />
                </a>
              );
            case IconEnum.Resume:
              return (
                <a
                  key={'aR' + rowData.original._id_}
                  href="#self"
                  onClick={(e: any) => {
                    e.preventDefault();
                    button.onClick(rowData, e);
                  }}
                  title={button.tooltip}
                >
                  <ResumeLogo
                    key={'R' + rowData.original._id_}
                    data-testid={'R' + rowData.original._id_}
                    style={getButtonStyle(button.color)}
                  />
                </a>
              );
            default:
              return (
                <a
                  key={'aE' + rowData.original._id_}
                  href="#self"
                  onClick={(e: any) => {
                    e.preventDefault();
                    button.onClick(rowData, e);
                  }}
                  title={button.tooltip}
                >
                  <Edit
                    key={'E' + rowData.original._id_}
                    data-testid={'E' + rowData.original._id_}
                    style={getButtonStyle(button.color)}
                  />
                </a>
              );
          }
        } else {
          return <></>;
        }
      })}
    </span>
  );
}

function generateHeaderButtons(editButtonsHeaderRef: React.MutableRefObject<any[]>, props: IProps): any {
  const editButtonsHeader = editButtonsHeaderRef.current;
  const headerStyle: any = {
    alignItems: 'center',
    display: 'block',
    height: 'auto',
    justifyContent: 'center',
    textAlign: 'center',
    verticalAlign: 'middle',
    width: 'auto',
  };
  return (
    <span style={headerStyle}>
      {' '}
      {editButtonsHeader.length === 0
        ? getTrimmedValue(props.iconColumnHeader)
        : editButtonsHeader.map((button: any, index: number) => {
            switch (button.iconType) {
              case IconEnum.Add:
                return (
                  <a
                    key={'aHA0'}
                    href="#self"
                    data-testid={button.tooltip}
                    onClick={(e: any) => {
                      e.preventDefault();
                      editButtonsHeaderRef.current[index].onClick();
                    }}
                    title={button.tooltip}
                  >
                    <Add key={'hA0'} data-testid={'AddIcon'} style={getHeaderButtonStyle(button.color)} />
                  </a>
                );
              case IconEnum.Delete:
                return (
                  <a
                    key={'aHD0'}
                    href="#self"
                    data-testid={button.tooltip}
                    onClick={(e: any) => {
                      e.preventDefault();
                      editButtonsHeaderRef.current[index].onClick();
                    }}
                    title={button.tooltip}
                  >
                    <Delete data-testid={'DeleteIcon'} key={'hD0'} style={getHeaderButtonStyle(button.color)} />
                  </a>
                );
              case IconEnum.PasteFromClipboard:
                return (
                  <a
                    key={'aHP0'}
                    data-testid={button.tooltip}
                    href="#self"
                    onClick={(e: any) => {
                      e.preventDefault();
                      editButtonsHeaderRef.current[index].onClick();
                    }}
                    title={button.tooltip}
                  >
                    <PasteFromClipboard
                      data-testid={'PasteIcon'}
                      key={'hP0'}
                      style={getHeaderButtonStyle(button.color)}
                    />
                  </a>
                );
              case IconEnum.Edit:
                return Edit;
              default:
                return <></>;
            }
          })}
    </span>
  );
}

const getStorageKey = (dataObject: IDataObject, displayType: string, gridTitle?: string) => {
  if (displayType === VERTICAL) {
    return undefined;
  }
  if (dataObject.sqlCommandText) {
    const title = gridTitle ? gridTitle.replace(/[^a-zA-Z0-9]/g, '') : '';
    const execCommandAry = dataObject.sqlCommandText.toUpperCase().split('EXEC ');
    if (execCommandAry.length > 1) {
      const spNameAry = execCommandAry[1].split(' ');
      return spNameAry[0] + '.' + title;
    }
  }
  return undefined;
};

export const generatePageSizeOptions = (pageSizeOptions: any[], dataLength: number) => {
  if (pageSizeOptions[pageSizeOptions.length - 1] !== dataLength) {
    if (dataLength > 100) {
      pageSizeOptions = [10, 20, 25, 50, 100];
      if (dataLength < 200) {
        pageSizeOptions.push(dataLength);
      } else if (pageSizeOptions[pageSizeOptions.length - 1] !== 200) {
        pageSizeOptions.push(200);
      }
    } else if (dataLength < 100 && dataLength >= 50) {
      pageSizeOptions = [10, 20, 25, 50];
      if (dataLength !== 50) {
        pageSizeOptions.push(dataLength);
      }
    } else if (dataLength < 50 && dataLength >= 25) {
      pageSizeOptions = [10, 20, 25];
      if (dataLength !== 25) {
        pageSizeOptions.push(dataLength);
      }
    } else if (dataLength < 25 && dataLength >= 20) {
      pageSizeOptions = [10, 20];
      if (dataLength !== 20) {
        pageSizeOptions.push(dataLength);
      }
    } else if (dataLength < 20 && dataLength >= 10) {
      pageSizeOptions = [10];
      if (dataLength !== 10) {
        pageSizeOptions.push(dataLength);
      }
    } else if (dataLength < 10) {
      pageSizeOptions = [];
      pageSizeOptions.push(dataLength);
    }
  }

  return pageSizeOptions;
};

const setPageSize = (pageSizeOptions: any[], pageSize: number, storageKey?: string, pageSizeDefault?: number) => {
  if (!storageKey) {
    return;
  }
  const localStoreJson = window.localStorage.getItem(storageKey);
  if (!localStoreJson) {
    return;
  }
  const localStoreInfo = JSON.parse(localStoreJson);

  if (pageSizeDefault !== -1 && pageSizeOptions.includes(localStoreInfo.pageSize)) {
    return;
  }
  localStoreInfo.pageSize = pageSize;
  window.localStorage.setItem(storageKey, JSON.stringify(localStoreInfo));
};

const removeSelectedRows = (storageKey?: string) => {
  if (!storageKey) {
    return;
  }
  const localStoreJson = window.localStorage.getItem(storageKey);
  if (!localStoreJson) {
    return;
  }
  const localStoreInfo = JSON.parse(localStoreJson);
  if (typeof localStoreInfo === 'object') {
    let selectedRowExist: boolean = false;
    if (localStoreInfo.hasOwnProperty('selectedRowKeys')) {
      delete localStoreInfo.selectedRowKeys;
      selectedRowExist = true;
    }
    if (localStoreInfo.hasOwnProperty('focusedRowKey')) {
      delete localStoreInfo.focusedRowKey;
      selectedRowExist = true;
    }
    if (selectedRowExist) {
      window.localStorage.setItem(storageKey, JSON.stringify(localStoreInfo));
    }
  }
};

const customSave = (storageKey?: string, state?: any, viewSource?: string) => {
  if (!storageKey || !state) {
    return;
  }
  state = { ...state, viewSource };
  window.localStorage.setItem(storageKey, JSON.stringify(state));
};

const customLoad = (storageKey?: string, viewSource?: string) => {
  if (!storageKey) {
    return;
  }
  const localStoreJson = window.localStorage.getItem(storageKey);
  if (!localStoreJson) {
    return {};
  }
  const localStoreInfo = JSON.parse(localStoreJson);
  if (storageKey === 'RPT_LABELING_METRICS.') {
    delete localStoreInfo.columns;
  }
  if (localStoreInfo.viewSource !== viewSource) {
    delete localStoreInfo.pageSize;
    delete localStoreInfo.pageIndex;
    delete localStoreInfo.filterValue;
  }
  return localStoreInfo;
};

const resetStore = (storageKey?: string) => {
  if (!storageKey) {
    return;
  }
  window.localStorage.removeItem(storageKey);
};

const getGroupPanelCount = (data: any[], extraColumnProps?: any) => {
  const uniqueSet = new Set();
  if (extraColumnProps) {
    const groupColumns = Object.keys(extraColumnProps).filter(
      (key: string) => extraColumnProps[key].groupIndex !== undefined,
    );
    if (groupColumns.length) {
      groupColumns.sort((a: string, b: string) =>
        extraColumnProps[a].groupIndex < extraColumnProps[b].groupIndex ? -1 : 1,
      );
      data.forEach((row: any) => {
        let key = '';
        groupColumns.forEach((column: string) => {
          key += `_${row[column]}`;
          uniqueSet.add(key);
        });
      });
    }
  }
  return uniqueSet.size;
};

const textAreaEditCellComponent = (event: any) => {
  if (event) {
    return (
      <TextArea
        height="110px"
        value={event.data.value}
        onKeyDown={(args: any) => {
          if (args.event.keyCode === 13) {
            args.event.stopPropagation();
          }
        }}
        className="inline-textArea"
        onValueChanged={(e: any) => {
          if (e && e.event && (e.value || e.value === '')) {
            event.data.setValue(e.value);
          }
        }}
      />
    );
  }
};

const calculateCustomSummary = (options: any) => {
  if (options.name === 'custom-count') {
    switch (options.summaryProcess) {
      case 'start':
        options.totalValue = 0;
        break;
      case 'calculate':
        options.totalValue += 1;
        break;
    }
  }
};

const mapStateToProps = (stateFromStore: any) => {
  return {
    allowAdd: stateFromStore.globalRdcr.allowAdd,
    allowDelete: stateFromStore.globalRdcr.allowDelete,
    allowEdit: stateFromStore.globalRdcr.allowEdit,
    heightToRemove: stateFromStore.globalRdcr.heightToRemove,
    userGroups: stateFromStore.globalRdcr.userGroups,
  };
};

export default connect(mapStateToProps)(GridControl);
